import { Query } from '@feathersjs/feathers';
import { Moment } from 'moment-timezone';
import { useCallback } from 'react';
import { GroupBase, OptionsOrGroups } from 'react-select';

import feathersClient from '../../../../../apiClient';
import TyAsyncSelect, { TyAsyncSelectProps } from '../../../../../components/inputs/TyAsyncSelect';
import { CompanyData } from '../../../../../types/TymbeApi';

type ProvidedProps = 'loadOptions' | 'getOptionLabel' | 'getOptionValue';

type InternalProps<Fields extends object> =
  TyAsyncSelectProps<Fields, CompanyData, GroupBase<CompanyData>, void, boolean>;
type SelectEmployerProps<Fields extends object> =
  Omit<InternalProps<Fields>, ProvidedProps> & {
    personId: number;
    from?: Moment;
    to?: Moment;
  };

const loadEmployerOptions = (
  personId: number,
  from?: Moment,
  to?: Moment,
  defaultOptionsCount?: number,
) => async (
  search: string,
  options: OptionsOrGroups<CompanyData, GroupBase<CompanyData>>,
) => {
  const query: Query = {
    $skip: options.length - (defaultOptionsCount || 0),
    $modify: {
      employs: [{
        personId,
        from: from?.toISOString(),
        to: to?.toISOString(),
      }],
    },
  };

  const iLike = { $ilike: `%${search}%` };
  if (search !== '' && !Number.isNaN(Number(search))) {
    query.id = Number(search);
  } else {
    query.$or = [{ name: iLike }, { display_name: iLike }];
  }

  const resp = await feathersClient.service('company').find({
    query,
  });

  return {
    options: resp.data,
    hasMore: resp.total > options.length + resp.data.length,
  };
};

const SelectEmployer = <Fields extends object>({
  debounceTimeout = 1000,
  defaultOptions = [],
  personId,
  from,
  to,
  ...props
}: SelectEmployerProps<Fields>) => {
  const defaultOptionsCount = Array.isArray(defaultOptions) ? defaultOptions.length : 0;
  const loadOptions = useCallback(
    () => loadEmployerOptions(personId, from, to, defaultOptionsCount),
    [personId, from, to, defaultOptionsCount],
  );

  return (
    <TyAsyncSelect
      {...props}
      cacheUniqs={[personId, from, to, defaultOptionsCount]}
      debounceTimeout={debounceTimeout}
      defaultOptions={defaultOptions}
      loadOptions={loadOptions()}
      getOptionLabel={(option) => `[${option.id}] ${option.name}`}
      getOptionValue={(option) => String(option.id)}
    />
  );
};

export default SelectEmployer as typeof SelectEmployer;
